import {
  DataBlockDictionaryColumnDto,
  DataBlockDictionaryDto,
  DataType,
  QueryFactory,
} from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';
import {
  onlyActiveDictionaries,
  useDataTypesList,
} from '../../../measurement-fields/common';
import { ColumnData, DictionaryData } from './common';

import styles from './ConfigureColumns.module.scss';

type TableColumnProps = {
  index: number;
  form: UseFormReturn<DictionaryData>;
  column: ColumnData;
};

export const DictionaryColumn: React.FC<TableColumnProps> = (props) => {
  const { index, form, column } = props;
  const i18n = useScopedTranslation('custom_dictionary.configure');
  const i18nDataBlockFields = useTranslation(NS_DataBlockFields);

  const dataTypes = useDataTypesList({
    usage: 'customDictionaryColumn',
  });

  const { data: referenceDictionaries } =
    QueryFactory.DataBlockDictionaryQuery.useGetQuery({
      select: onlyActiveDictionaries,
    });

  const sortedDictionaries = useMemo(() => {
    return referenceDictionaries?.sort((a, b) =>
      i18nDataBlockFields
        .t(a.title as any)
        .localeCompare(i18nDataBlockFields.t(b.title as any)),
    );
  }, [referenceDictionaries]);

  const fieldType = form.watch(`columns.${index}.dataType`) as DataType;

  return (
    <>
      <input
        type="hidden"
        {...form.register(`columns.${index}.id`)}
        defaultValue={column.id || ''}
      />
      <div>
        <Field
          className={styles.columnField}
          title={i18n.t('column_title', { index: index + 1 })}
          required={true}
        >
          <Input
            {...form.register(`columns.${index}.title`)}
            autoComplete="off"
            placeholder={i18n.t('column_title_placeholder')}
            errorText={form.formState.errors.columns?.[index]?.title?.message}
            defaultValue={column.title}
            disabled={column.required}
          />
        </Field>
        <Field className={styles.columnField} title="">
          <CheckBox
            className={styles.uniqueValuesCheckbox}
            title={i18n.t('column_is_unique')}
            {...form.register(`columns.${index}.isUnique`)}
          />
        </Field>
      </div>
      <Field title={i18n.t('column_type')} className={styles.columnField}>
        <HookFormDropDownInput
          enableSearch={true}
          control={form.control}
          defaultValue={undefined}
          name={`columns.${index}.dataType`}
          required
          variant="formInput"
          getOptionLabel="displayValue"
          idFunction="value"
          options={dataTypes}
          errorText={form.formState.errors.columns?.[index]?.dataType?.message}
          disabled={column.id != null}
        />
      </Field>
      {fieldType === DataType.Dictionary && (
        <Field
          className={styles.columnField}
          title={i18n.t('dictionary')}
          required={true}
        >
          <HookFormDropDownInput
            control={form.control}
            rules={requiredRule()}
            name={`columns.${index}.referenceDictionaryId`}
            enableSearch={true}
            getOptionLabel={(item) =>
              i18nDataBlockFields.t(item.title as any, {
                nsSeparator: false,
              })
            }
            required
            disabled={column.id != null}
            idFunction="id"
            options={sortedDictionaries ?? []}
            errorText={
              form.formState.errors.columns?.[index]?.referenceDictionaryId
                ?.message
            }
          />
        </Field>
      )}
    </>
  );
};
